/**
 * LoginPage — full version (Faza 1).
 * Tryby: login | register | forgot.
 * Login/Register switcher: SegmentedControl fullWidth (V2 design system).
 * Logika 1:1 z js/auth.js: Google OAuth (popup/redirect po UA), password strength,
 * remember me, prefill z Google name, dev mode bypass.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../../components/Icons';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { showToast } from '../../components/ui/Toast';
import { useTheme } from '../../lib/ThemeProvider';
import { IS_DEV_MODE } from '../../lib/firebase';
import {
  signIn,
  signUp,
  sendPasswordReset,
  signInWithGoogle,
  consumeGoogleRedirect,
  getAuthErrorMessage,
} from '../../features/auth/api';
import { useAuthStore } from '../../features/auth/authStore';
import { devSignIn } from '../../features/auth/useAuthSync';
import { validateEmail, validatePassword } from '../../lib/utils';

type Mode = 'login' | 'register' | 'forgot';

const DEV_PREFILL = { email: 'dev@example.com', password: 'dev123', name: 'Dev User' };

const GoogleLogo = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z" fill="#4285F4" />
    <path d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2c-.72.48-1.64.78-2.7.78-2.08 0-3.84-1.4-4.47-3.3H1.83v2.07A8 8 0 0 0 8.98 17z" fill="#34A853" />
    <path d="M4.5 10.55a4.8 4.8 0 0 1 0-3.07V5.4H1.83a8 8 0 0 0 0 7.18l2.67-2.04z" fill="#FBBC05" />
    <path d="M8.98 4.18c1.18 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4l2.67 2.07c.63-1.9 2.39-3.3 4.48-3.3z" fill="#EA4335" />
  </svg>
);

const getPasswordStrength = (pw: string): { score: number; label: string; color: string } => {
  if (pw.length === 0) return { score: 0, label: '', color: 'bg-slate-200' };
  if (pw.length < 8) return { score: 0, label: 'Zbyt krótkie (min. 8 znaków)', color: 'bg-rose-400' };
  let s = 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  if (s === 1) return { score: 1, label: 'Słabe', color: 'bg-rose-500' };
  if (s === 2) return { score: 2, label: 'Średnie', color: 'bg-amber-500' };
  return { score: 3, label: 'Silne', color: 'bg-emerald-500' };
};

export function LoginPage() {
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const setPendingRegistration = useAuthStore((s) => s.setPendingRegistration);
  const setGoogleNamePrefill = useAuthStore((s) => s.setGoogleNamePrefill);
  const googleNamePrefill = useAuthStore((s) => s.googleNamePrefill);

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState(IS_DEV_MODE ? DEV_PREFILL.email : '');
  const [password, setPassword] = useState(IS_DEV_MODE ? DEV_PREFILL.password : '');
  const [name, setName] = useState(IS_DEV_MODE ? DEV_PREFILL.name : googleNamePrefill ?? '');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Po powrocie z Google redirect — sprawdź wynik (ignoruj jeśli brak)
  useEffect(() => {
    if (IS_DEV_MODE) return;
    consumeGoogleRedirect()
      .then((result) => {
        if (!result) {
          console.info('[Login] consumeGoogleRedirect: brak wyniku (zwykle pierwszy mount, OK)');
          return;
        }
        console.info('[Login] redirect SUCCESS, user:', result.user.uid);
        setGoogleNamePrefill(result.user.displayName ?? '');
        useAuthStore.setState({
          user: {
            uid: result.user.uid,
            email: result.user.email ?? '',
            name: result.user.displayName ?? '',
          },
        });
        navigate('/dashboard');
      })
      .catch((err) => {
        console.error('[Login] redirect ERROR:', err.code, err.message);
        const code = err?.code;
        // auth/unauthorized-domain — domena nie ma whitelist w Firebase Console
        if (code === 'auth/unauthorized-domain') {
          setError('Domena nie jest autoryzowana w Firebase Auth. Dodaj rodzinkafinance.pl w Firebase Console → Authentication → Settings → Authorized domains.');
          return;
        }
        setError(getAuthErrorMessage(code) + ` (${code ?? 'unknown'})`);
      });
  }, [setGoogleNamePrefill, navigate]);

  const switchMode = (m: Mode) => {
    setMode(m);
    setError('');
    setSuccess('');
    if (m !== 'register') setPassword('');
  };

  const handleGoogleAuth = async () => {
    console.info('[Login] handleGoogleAuth START', { ua: navigator.userAgent.slice(0, 80) });
    setError('');
    setLoading(true);
    try {
      if (IS_DEV_MODE) {
        await new Promise((r) => setTimeout(r, 300));
        devSignIn();
        showToast('Zalogowano przez Google (DEV)', 'success');
        navigate('/dashboard');
        return;
      }
      console.info('[Login] calling signInWithGoogle...');
      const result = await signInWithGoogle();
      console.info('[Login] signInWithGoogle returned:', result ? 'POPUP success' : 'REDIRECT (page reloads)');
      if (!result) return; // redirect — strona przeładuje się
      console.info('[Login] setting user in store, uid=', result.user.uid, 'email=', result.user.email);
      setGoogleNamePrefill(result.user.displayName ?? '');
      useAuthStore.setState({
        user: {
          uid: result.user.uid,
          email: result.user.email ?? '',
          name: result.user.displayName ?? '',
        },
      });
      console.info('[Login] navigate to /dashboard NOW');
      navigate('/dashboard');
      console.info('[Login] navigate called, current store state:', {
        user: useAuthStore.getState().user?.uid,
        family: useAuthStore.getState().family?.id ?? 'NULL (oczekuję useAuthSync findUserFamily)',
        authReady: useAuthStore.getState().authReady,
      });
    } catch (err) {
      const errObj = err as { code?: string; message?: string };
      const code = errObj.code;
      console.error('[Login] Google auth FAILED:', code, errObj.message, err);
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        const msg = getAuthErrorMessage(code);
        setError(`${msg} (${code ?? 'no-code'})`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Walidacja
    if (mode !== 'forgot' && !validateEmail(email)) {
      setError('Podaj poprawny adres email');
      return;
    }
    if (mode === 'register' && name.trim().length < 1) {
      setError('Podaj imię');
      return;
    }
    if (mode !== 'forgot' && !IS_DEV_MODE && !validatePassword(password).valid) {
      setError('Hasło musi mieć min. 6 znaków');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        if (IS_DEV_MODE) {
          await new Promise((r) => setTimeout(r, 300));
          devSignIn();
          showToast('Zalogowano (DEV)', 'success');
          navigate('/dashboard');
          return;
        }
        await signIn(email, password, rememberMe);
        // Manual navigate — RedirectIfAuthenticated wymaga user+family.
        // Family lookup w useAuthSync trwa async, więc sami pchamy /dashboard
        // żeby RequireAuthAndFamily ogarnął redirect (do /register-family
        // jeśli brak rodziny, do /dashboard jeśli jest).
        navigate('/dashboard');
      } else if (mode === 'register') {
        if (IS_DEV_MODE) {
          await new Promise((r) => setTimeout(r, 300));
          setPendingRegistration({ name, email });
          navigate('/register-family');
          return;
        }
        await signUp(email, password, name);
        setPendingRegistration({ name, email });
        navigate('/register-family');
      } else if (mode === 'forgot') {
        if (IS_DEV_MODE) {
          await new Promise((r) => setTimeout(r, 400));
          setSuccess('Jeśli konto istnieje, wysłaliśmy link resetujący (DEV — symulacja).');
          return;
        }
        await sendPasswordReset(email).catch(() => {
          /* silent — nie ujawniaj czy email istnieje */
        });
        setSuccess('Jeśli konto z tym adresem istnieje, wysłaliśmy link do resetowania hasła. Sprawdź skrzynkę pocztową (i folder spam).');
      }
    } catch (err) {
      setError(getAuthErrorMessage((err as { code?: string }).code));
    } finally {
      setLoading(false);
    }
  };

  const strength = mode === 'register' ? getPasswordStrength(password) : null;
  const submitLabel =
    mode === 'login' ? 'Zaloguj się' : mode === 'register' ? 'Załóż konto' : 'Wyślij link resetujący';

  return (
    <main className="min-h-screen bg-slate-100 dark:bg-slate-900 flex flex-col items-center justify-center p-4 safe-area-bottom">
      <button
        onClick={toggle}
        className="fixed top-4 right-4 z-50 p-2.5 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur border border-slate-200 dark:border-slate-700 shadow-card text-slate-600 dark:text-slate-300 hover:scale-105 transition-all"
        aria-label="Przełącz motyw"
      >
        {theme === 'dark' ? <Icons.Sun size={18} /> : <Icons.Moon size={18} />}
      </button>

      {/* Logo */}
      <div className="text-center mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl mx-auto mb-4 bg-gradient-to-br from-sky-500 via-sky-600 to-emerald-500 shadow-lg shadow-sky-500/30 flex items-center justify-center">
          <Icons.Heart size={56} className="text-white" strokeWidth={2} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900 dark:text-white tracking-tight">
          Rodzinka Finance
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1.5 text-sm sm:text-base">
          Zarządzanie budżetem rodzinnym
        </p>
      </div>

      <div className="w-full max-w-sm bg-white dark:bg-slate-800 rounded-3xl shadow-card-hover dark:shadow-2xl border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Forgot password header z return */}
        {mode === 'forgot' ? (
          <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-700">
            <button
              onClick={() => switchMode('login')}
              className="flex items-center gap-1.5 text-sm font-medium text-sky-600 dark:text-sky-300 mb-3"
            >
              <Icons.ChevronLeft size={16} />
              Wróć do logowania
            </button>
            <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white tracking-tight">
              Resetowanie hasła
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Wyślemy link resetujący na Twój email.
            </p>
            <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-2">
              <Icons.AlertCircle size={14} className="text-amber-600 dark:text-amber-300 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                <strong>Sprawdź folder SPAM</strong> — wiadomości z Firebase często trafiają tam zamiast do skrzynki głównej.
              </p>
            </div>
          </div>
        ) : (
          <div className="px-4 pt-4 pb-2">
            <SegmentedControl<Mode>
              fullWidth
              ariaLabel="Wybierz tryb"
              options={[
                { key: 'login', label: 'Logowanie' },
                { key: 'register', label: 'Rejestracja' },
              ]}
              value={mode}
              onChange={switchMode}
            />
          </div>
        )}

        {/* OAuth */}
        {mode !== 'forgot' && (
          <div className="px-6 pt-4 pb-2 space-y-3">
            <button
              type="button"
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full py-3.5 flex items-center justify-center gap-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-600 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              aria-label={mode === 'login' ? 'Zaloguj przez Google' : 'Zarejestruj przez Google'}
            >
              <GoogleLogo size={18} />
              <span>{mode === 'login' ? 'Zaloguj się przez Google' : 'Zarejestruj się przez Google'}</span>
            </button>

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">lub</span>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-6 pt-2 pb-6 space-y-4">
          {mode === 'register' && (
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Imię</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Anna"
                autoComplete="given-name"
                required
                className="w-full px-4 py-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition-all text-base"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Adres email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="anna@rodzinka.pl"
              autoComplete="username"
              inputMode="email"
              required
              className="w-full px-4 py-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition-all text-base"
            />
          </div>

          {mode !== 'forgot' && (
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Hasło</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  required
                  minLength={IS_DEV_MODE ? 1 : 6}
                  className="w-full px-4 py-3.5 pr-12 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 transition-all text-base"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg"
                >
                  {showPassword ? <Icons.EyeOff size={20} /> : <Icons.Eye size={20} />}
                </button>
              </div>

              {mode === 'register' && password.length > 0 && strength && (
                <div className="space-y-1 pt-1">
                  <div className="flex gap-1.5">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                          strength.score >= i ? strength.color : 'bg-slate-200 dark:bg-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                  <p
                    className={`text-xs font-medium ${
                      strength.score >= 3
                        ? 'text-emerald-600 dark:text-emerald-300'
                        : strength.score === 2
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-rose-500'
                    }`}
                  >
                    {strength.label}
                  </p>
                </div>
              )}
            </div>
          )}

          {mode === 'login' && (
            <div className="flex items-center justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={() => setRememberMe((v) => !v)}
                className="flex items-center gap-2.5 flex-1 min-w-0 py-2"
              >
                <div
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                    rememberMe ? 'bg-sky-600 border-sky-600' : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500'
                  }`}
                >
                  {rememberMe && <Icons.Check size={12} className="text-white" strokeWidth={3} />}
                </div>
                <span className="text-sm text-slate-600 dark:text-slate-400 text-left leading-tight">
                  Zapamiętaj mnie
                </span>
              </button>
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="text-sm font-medium text-sky-600 dark:text-sky-300 shrink-0 py-2 px-1"
              >
                Zapomniałem hasła
              </button>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2.5 p-3.5 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl text-sm text-rose-600 dark:text-rose-300">
              <Icons.AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-start gap-2.5 p-3.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl text-sm text-emerald-700 dark:text-emerald-300">
              <Icons.Success size={16} className="shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          {!success && mode !== 'forgot' && (
            <div className="flex items-center justify-center gap-1.5 pt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
              <Icons.Shield size={14} className="text-emerald-600 dark:text-emerald-400" />
              <span>Bezpieczne połączenie</span>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <span>Firebase</span>
            </div>
          )}

          {!success && (
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 disabled:bg-sky-400 text-white font-bold rounded-xl transition-colors shadow-lg shadow-sky-600/25 text-base mt-2"
            >
              {loading ? 'Proszę czekać...' : submitLabel}
            </button>
          )}
        </form>
      </div>

      <p className="mt-6 text-xs text-slate-500 dark:text-slate-400 text-center">
        © {new Date().getFullYear()} Rodzinka Finance · Budżet rodziny w jednej apce
      </p>
    </main>
  );
}

export default LoginPage;
